<!--
 * Path: src/features/browser/forms/IconColorSelector.vue
 * Task: Reusable component providing a unified Material Design Icon input and color picker dropdown.
 * Update: Fix horizontal scrollbar issue on v-btn-toggle by disabling scale transform on hover.
-->
<template>
  <div>
    <v-row class="mt-2 align-center">
      <!-- Icon Input with Dynamic Preview -->
      <v-col cols="12" :md="showShape ? 5 : 6" class="py-1">
        <div class="d-flex align-center">
          <!-- Preview (Always uses v-avatar for consistency) -->
          <v-avatar
            :color="shapeModel === 'none' ? 'transparent' : (colorModel || 'grey')"
            :size="40"
            class="me-3 flex-shrink-0"
            :class="{ 'elevation-1': shapeModel !== 'none' }"
            :style="{
              borderRadius: shapeModel === 'circle' ? '50%' : (shapeModel === 'none' ? '0' : '10px'),
              border: shapeModel === 'none' ? 'none' : '1px solid rgba(128,128,128,0.2)'
            }"
          >
            <v-icon :color="shapeModel === 'none' ? (colorModel || 'grey') : 'white'">
              {{ iconModel || defaultIcon }}
            </v-icon>
          </v-avatar>

          <v-text-field
            v-model="iconModel"
            :label="iconLabel"
            type="text"
            variant="outlined"
            density="comfortable"
            :disabled="disabled"
            hide-details
            class="flex-grow-1"
            @keydown.stop
          >
            <!-- Info button linked to the MDI library dynamically -->
            <template v-slot:append-inner>
              <v-btn
                v-if="mdiLibraryUrl"
                icon
                variant="text"
                density="compact"
                size="small"
                color="medium-emphasis"
                @mousedown.stop
                @click.stop="openMdiLibrary"
              >
                <v-icon>mdi-information-outline</v-icon>
                <v-tooltip activator="parent" location="top">Open MDI Library</v-tooltip>
              </v-btn>
            </template>
          </v-text-field>
        </div>
      </v-col>

      <!-- Color Input -->
      <v-col cols="12" :md="showShape ? 4 : 6" class="py-1">
        <div class="d-flex align-center">
          <v-text-field
            v-model="colorModel"
            :label="colorLabel"
            type="text"
            variant="outlined"
            density="comfortable"
            :disabled="disabled"
            hide-details
            class="flex-grow-1"
            @keydown.stop
          >
            <!-- Palette button to open the Color Picker Menu -->
            <template v-slot:append-inner>
              <v-menu :close-on-content-click="false" location="bottom end" :disabled="disabled">
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon
                    variant="text"
                    density="compact"
                    size="small"
                    :color="colorModel || 'medium-emphasis'"
                    @mousedown.stop
                  >
                    <v-icon>mdi-palette</v-icon>
                    <v-tooltip activator="parent" location="top">Choose Color</v-tooltip>
                  </v-btn>
                </template>
                <v-card class="pa-2 bg-surface">
                  <v-color-picker
                    v-model="colorModel"
                    :modes="['hex']"
                    hide-inputs
                    flat
                    elevation="0"
                  ></v-color-picker>
                </v-card>
              </v-menu>
            </template>
          </v-text-field>
        </div>
      </v-col>

      <!-- Avatar Shape Toggle (Dynamically generated from app-data.json) -->
      <v-col v-if="showShape" cols="12" md="3" class="py-1 d-flex align-center justify-md-end">
        <v-btn-toggle
          v-model="shapeModel"
          color="primary"
          mandatory
          variant="outlined"
          style="height: 48px;"
          class="shape-toggle"
          :disabled="disabled"
        >
          <v-btn
            v-for="shape in availableShapes"
            :key="shape.value"
            :value="shape.value"
            min-width="48"
            class="px-0 no-scale"
          >
            <v-icon>{{ shape.icon }}</v-icon>
            <v-tooltip activator="parent" location="top">{{ shape.label }}</v-tooltip>
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '@/store/auth';

interface Props {
  iconLabel?: string;
  colorLabel?: string;
  defaultIcon?: string;
  disabled?: boolean;
  showShape?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  iconLabel: 'Icon',
  colorLabel: 'Color',
  defaultIcon: 'mdi-help-circle-outline',
  disabled: false,
  showShape: false
});

// Vue 3.4+ Two-Way Binding Macros
const iconModel = defineModel<string>('icon', { default: '' });
const colorModel = defineModel<string>('color', { default: '' });
const shapeModel = defineModel<string>('shape', { default: 'squircle' });

const authStore = useAuthStore();

// Dynamically fetch available shapes from the global app settings
const availableShapes = computed(() => {
  const shapesConfig = authStore.appData?.iconShapes || {};
  const shapes = [];
  for (const key in shapesConfig) {
    shapes.push({
      value: key,
      label: shapesConfig[key].label || key,
      icon: shapesConfig[key].icon || 'mdi-shape'
    });
  }
  // Provide a safe fallback if the app-data is incomplete or loading
  if (shapes.length === 0) {
    return [
      { value: 'squircle', label: 'Squircle', icon: 'mdi-square-rounded' },
      { value: 'circle', label: 'Circle', icon: 'mdi-circle' },
      { value: 'none', label: 'None (Icon Only)', icon: 'mdi-star-four-points-outline' }
    ];
  }
  return shapes;
});


// Dynamically fetch the MDI library URL from the global app settings
const mdiLibraryUrl = computed(() => authStore.appData?.mdiLibraryUrl || 'https://pictogrammers.com/library/mdi/');

const openMdiLibrary = () => {
  if (mdiLibraryUrl.value) {
    window.open(mdiLibraryUrl.value, '_blank');
  }
};
</script>

<style scoped>
/* Smooth visual transitions */
.v-btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* Prevent scaling on explicitly marked buttons (like toggle groups) */
.v-btn:not(.no-scale):hover:not(:disabled) {
  transform: scale(1.1);
}

/* Hide scrollbar completely to prevent layout breaking during subpixel rendering */
.shape-toggle {
  overflow-x: hidden !important;
  scrollbar-width: none;
}
.shape-toggle::-webkit-scrollbar {
  display: none;
}
</style>
